import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

/**
 * Option for {@link SegmentedControl}.
 *
 * @example
 * { id: 'all', label: 'All', count: 4 }
 * { id: 'invoice', label: 'Invoice', count: 1 }
 */
export type SegmentedControlOption<T extends string = string> = {
  id: T;
  label: string;
  icon?: React.ReactNode;
  /** Count badge after the label (shown when defined; use `hideZeroCounts` to suppress 0). */
  count?: number;
  disabled?: boolean;
};

export type SegmentedControlProps<T extends string = string> = {
  options: Array<SegmentedControlOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Extra controls rendered inside the same pill bar (e.g. a secondary link). */
  trailing?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md';
  /** When true, omit badges whose count is 0. Default: false (show 0). */
  hideZeroCounts?: boolean;
  /** Stretch the bar to full container width. */
  fullWidth?: boolean;
  /** Accessible name for the tablist. */
  'aria-label'?: string;
};

/**
 * Soft track + elevated active pill with count badges.
 * Matches the ATLAS filter chip pattern used across list/inbox screens.
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  trailing,
  className,
  size = 'md',
  hideZeroCounts = false,
  fullWidth = false,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';

  const pad = size === 'sm' ? 'gap-1.5 px-3.5 py-1.5 text-xs' : 'gap-2 px-4 py-2 text-sm';
  const badgePad =
    size === 'sm'
      ? 'min-w-[1.15rem] px-1.5 py-[0.2rem] text-[10px]'
      : 'min-w-[1.25rem] px-1.5 py-0.5 text-[11px]';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex max-w-full items-center rounded-xl p-1',
        isDark
          ? 'bg-white/[0.06] ring-1 ring-white/10'
          : 'bg-slate-100/90 ring-1 ring-slate-200/80',
        fullWidth && 'w-full',
        trailing ? 'overflow-visible' : 'overflow-x-auto',
        className,
      )}
    >
      <div
        className={cn(
          'inline-flex items-center gap-0.5',
          fullWidth && 'w-full',
          trailing ? 'min-w-0 overflow-x-auto' : undefined,
        )}
      >
        {options.map((option) => {
          const active = value === option.id;
          const showCount =
            option.count != null && !(hideZeroCounts && option.count === 0);

          return (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={option.disabled}
              onClick={() => onChange(option.id)}
              className={cn(
                'inline-flex shrink-0 items-center rounded-lg font-medium transition-all duration-150',
                pad,
                fullWidth && 'flex-1 justify-center',
                active
                  ? cn(
                      colors.surface,
                      colors.text,
                      'shadow-sm',
                      isDark
                        ? 'ring-1 ring-white/15'
                        : 'ring-1 ring-slate-200/90',
                    )
                  : cn(
                      'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      'hover:bg-white/50 dark:hover:bg-white/[0.04]',
                    ),
                option.disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
              )}
            >
              {option.icon != null && (
                <span
                  className={cn(
                    'shrink-0',
                    active ? colors.primaryFg : 'opacity-60',
                  )}
                >
                  {option.icon}
                </span>
              )}
              <span className="whitespace-nowrap tracking-tight">{option.label}</span>
              {showCount && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center rounded-full font-semibold tabular-nums leading-none',
                    badgePad,
                    active
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : isDark
                        ? 'border border-white/10 bg-[#121a2b] text-slate-300'
                        : 'border border-slate-200/90 bg-white text-slate-600',
                  )}
                >
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {trailing != null && (
        <div className="ml-1 flex shrink-0 items-center gap-0.5 pl-1">{trailing}</div>
      )}
    </div>
  );
}
